import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import type { PointerEvent, RefObject } from 'react';
export type TFloatingPosition = Readonly<{
  x: number;
  y: number;
}>;
type TClampFloatingPositionArgs = Readonly<{
  panelWidth: number;
  panelHeight: number;
  edgeThreshold: number;
  viewportWidth: number;
  viewportHeight: number;
}>;
type UseDraggableOptions = {
  panelWidth?: number;
  edgeThreshold?: number;
  panelRef?: RefObject<Nullable<HTMLElement>>;
};
type UseDraggableReturn = {
  position: Nullable<TFloatingPosition>;
  setPosition: (pos: TFloatingPosition) => void;
  isDragging: boolean;
  handleDragStart: (e: PointerEvent<HTMLElement>) => void;
};
export const clampFloatingPosition = (position: TFloatingPosition, {
  panelWidth,
  panelHeight,
  edgeThreshold,
  viewportWidth,
  viewportHeight
}: TClampFloatingPositionArgs): TFloatingPosition => ({
  x: Math.max(-(panelWidth - edgeThreshold), Math.min(viewportWidth - edgeThreshold, position.x)),
  y: Math.max(-(panelHeight - edgeThreshold), Math.min(viewportHeight - edgeThreshold, position.y))
});
export function useDraggable(options) {
  const $ = _c(21);
  const panelWidth = options?.panelWidth ?? 480;
  const edgeThreshold = options?.edgeThreshold ?? 40;
  const panelRef = options?.panelRef;
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState(null);
  const isDraggingRef = useRef(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      x: 0,
      y: 0
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const dragOriginRef = useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      x: 0,
      y: 0
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const positionAtDragStartRef = useRef(t1);
  let t2;
  if ($[2] !== position) {
    t2 = e => {
      if ((e.target as HTMLElement).closest("button") !== null) {
        return;
      }
      e.preventDefault();
      isDraggingRef.current = true;
      setIsDragging(true);
      dragOriginRef.current = {
        x: e.clientX,
        y: e.clientY
      };
      positionAtDragStartRef.current = position ?? {
        x: 0,
        y: 0
      };
    };
    $[2] = position;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleDragStart = t2;
  let t3;
  if ($[4] !== edgeThreshold || $[5] !== isDragging || $[6] !== panelRef?.current || $[7] !== panelWidth) {
    t3 = () => {
      if (!isDragging) {
        return;
      }
      const origin = dragOriginRef.current;
      const startPos = positionAtDragStartRef.current;
      const onMove = ev => {
        const rect = panelRef?.current?.getBoundingClientRect();
        setPosition(clampFloatingPosition({
          x: startPos.x + (ev.clientX - origin.x),
          y: startPos.y + (ev.clientY - origin.y)
        }, {
          panelWidth: rect?.width ?? panelWidth,
          panelHeight: rect?.height ?? edgeThreshold,
          edgeThreshold,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight
        }));
      };
      const onUp = () => {
        isDraggingRef.current = false;
        setIsDragging(false);
      };
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
      return () => {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
      };
    };
    $[4] = edgeThreshold;
    $[5] = isDragging;
    $[6] = panelRef?.current;
    $[7] = panelWidth;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== edgeThreshold || $[10] !== isDragging || $[11] !== panelRef || $[12] !== panelWidth) {
    t4 = [isDragging, panelWidth, edgeThreshold, panelRef];
    $[9] = edgeThreshold;
    $[10] = isDragging;
    $[11] = panelRef;
    $[12] = panelWidth;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[14] !== isDragging) {
    t5 = () => {
      document.body.style.cursor = isDragging ? "grabbing" : "";
      document.body.style.userSelect = isDragging ? "none" : "";
      return _temp;
    };
    t6 = [isDragging];
    $[14] = isDragging;
    $[15] = t5;
    $[16] = t6;
  } else {
    t5 = $[15];
    t6 = $[16];
  }
  useEffect(t5, t6);
  let t7;
  if ($[17] !== handleDragStart || $[18] !== isDragging || $[19] !== position) {
    t7 = {
      position,
      setPosition,
      isDragging,
      handleDragStart
    };
    $[17] = handleDragStart;
    $[18] = isDragging;
    $[19] = position;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}
function _temp() {
  document.body.style.cursor = "";
  document.body.style.userSelect = "";
}